import { useState } from 'react'

import { createBankSetupIntent, getStripe, savePaymentMethod } from '../lib/stripe.js'

// Drives the full Stripe ACH bank-account setup flow:
//   1. Server creates Stripe Customer (if needed) + SetupIntent → client_secret
//   2. Stripe.js opens the Financial Connections / manual account UI
//   3. On success Stripe.js confirms the SetupIntent automatically
//   4. We POST the pm_… back to the server to persist on the customer
//
// Until Cloud Functions are live (project must be on Blaze), the click
// surfaces a clear error in the flash slot — no silent failures.
export default function ConnectBankButton({ customer, customerId, onResult }) {
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null)

  const connect = async () => {
    setBusy(true); setFlash(null)
    try {
      const stripe = await getStripe()
      if (!stripe) throw new Error('Stripe.js could not load. Check VITE_STRIPE_PUBLISHABLE_KEY.')

      const { clientSecret } = await createBankSetupIntent(customerId)
      if (!clientSecret) throw new Error('Server did not return a client secret.')

      // payment_method_data is intentionally omitted: it's mutually
      // exclusive with payment_method_options.us_bank_account.
      // verification_method='instant' (Stripe rejects the pair with
      // "Payment method data is not allowed when ... verification_method=
      // instant"). Financial Connections pulls billing details from the
      // bank session itself, so nothing is lost.
      const result = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: { payment_method_type: 'us_bank_account' },
      })
      if (result.error) throw new Error(result.error.message || 'Stripe rejected the bank account.')
      const setupIntent = result.setupIntent
      if (!setupIntent) throw new Error('Stripe returned no SetupIntent.')

      // Confirm the SetupIntent so the bank account is fully set up.
      const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret)
      if (confirmed.error) throw new Error(confirmed.error.message || 'Setup confirmation failed.')

      const pm = confirmed.setupIntent?.payment_method
      if (!pm) throw new Error('No payment method ID returned.')

      await savePaymentMethod(customerId, pm)
      setFlash({ kind: 'ok', msg: '✓ Bank account connected.' })
      onResult?.({ ok: true, paymentMethodId: pm })
    } catch (e) {
      setFlash({ kind: 'err', msg: e?.message || 'Could not connect bank account.' })
      onResult?.({ ok: false, error: e })
    } finally {
      setBusy(false)
    }
  }

  const linked = !!customer?.stripePaymentMethodId
  return (
    <div>
      <button type="button" className="btn-secondary" onClick={connect} disabled={busy}>
        {busy ? 'Connecting…' : linked ? '🔁 Re-connect bank' : '🏦 Connect bank account'}
      </button>
      {flash ? (
        <p className={`text-xs font-bold mt-2 ${flash.kind === 'ok' ? 'text-brand-green-deep' : 'text-red-600'}`}>
          {flash.msg}
        </p>
      ) : null}
    </div>
  )
}
